import React, { useCallback } from 'react';
import { Redirect, useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { layout } from '@modernhealth/vital-foundation';
import { pxToRem } from '@modernhealth/vital-components';
import { captureEvent, Severity } from '@sentry/browser';
import { useTranslation } from 'react-i18next';

import { toastNotificationsService } from 'src/config/rootService';
import { MappedAnswers } from 'src/modules/assessments/models/assessments';
import { Assessment } from 'src/modules/assessments/components/Assessment';
import { CenteredSpinner } from 'src/shared/components/CenteredSpinner';
import { usePathwaysNavigation } from 'src/modules/pathways/hooks/navigation';
import { usePathwayQuery } from 'src/modules/pathways/documents/pathway.generated';
import { useSubmitPathwayAssessmentMutation } from 'src/modules/pathways/documents/submitPathwayAssessmentMutation.generated';

const Container = styled.div({
    width: '100%',
    maxWidth: layout.containerWidth,
    margin: '0 auto',
    padding: pxToRem(16, 0),
});

export const PathwayAssessmentPage = () => {
    const { uuid: pathwayUuid } = useParams<{ uuid: string }>();
    const { t } = useTranslation();
    const pathwaysNavigation = usePathwaysNavigation();
    const [submitPathwayAssessment] = useSubmitPathwayAssessmentMutation();

    const { data, loading } = usePathwayQuery({
        variables: {
            uuid: pathwayUuid,
        },
    });

    const pathway = data?.pathway;
    const memberPathway = pathway?.memberPathway;

    const handleSubmit = useCallback(
        async (answersOnly: MappedAnswers) => {
            // Handle this case while it's still technically possible. Ideally the backend should prevent this
            if (!pathway?.assessment.name) {
                captureEvent({
                    message: 'No assessment name found',
                    level: Severity.Error,
                    extra: { pathwayUuid },
                });
                toastNotificationsService.renderErrorToast(t('messaging:generic_error.title'));
                return;
            }

            const answers = { [pathway.assessment.name]: answersOnly };
            const { data } = await submitPathwayAssessment({
                variables: {
                    data: {
                        answers: JSON.stringify(answers),
                        pathwayUuid,
                    },
                },
            });

            if (data?.submitPathwayAssessment?.__typename !== 'PathwayAssessmentSubmitted') {
                const internalErrorMessage =
                    data?.submitPathwayAssessment?.__typename ||
                    'An error occured while submitting a pathways assessment';

                captureEvent({
                    message: internalErrorMessage,
                    level: Severity.Error,
                    extra: { pathwayUuid },
                });

                toastNotificationsService.renderErrorToast(t('messaging:generic_error.title'));
            }

            pathwaysNavigation.navigate.pathway({ uuid: pathwayUuid });
        },
        [
            pathway?.assessment.name,
            pathwayUuid,
            pathwaysNavigation.navigate,
            submitPathwayAssessment,
            t,
        ],
    );

    if (!data?.pathway || loading) {
        return (
            <Container>
                <CenteredSpinner />
            </Container>
        );
    }

    if (!memberPathway) {
        return <Redirect to={pathwaysNavigation.locationDescriptor.pathways()} />;
    }

    return (
        <Assessment
            assessmentName={pathway.assessment.name || ''}
            questions={pathway.assessment.assessmentQuestions}
            onSubmit={handleSubmit}
        />
    );
};
